import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { placementDriveService, vacancyService, applicationService, studentService } from '../../services/api';
import { getUserData } from '../../utils/auth';
import './CoordinatorDashboardHome.css';

function CoordinatorDashboardHome() {
  const userData = getUserData();
  const collegeId = userData?.collegeId || userData?.CollegeId;

  const [stats, setStats] = useState({ drives: 0, vacancies: 0, applications: 0, shortlisted: 0, pendingStudents: 0 });
  const [recentDrives, setRecentDrives] = useState([]);
  const [recentVacancies, setRecentVacancies] = useState([]);
  const [recentApplications, setRecentApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (collegeId) loadData();
    else setLoading(false);
  }, [collegeId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [drivesData, vacanciesData, appsData, pendingData] = await Promise.all([
        placementDriveService.GetPlacementDrivesByCollege(collegeId),
        vacancyService.getVacanciesByCollege(collegeId),
        applicationService.getApplicationsByCollege(collegeId),
        studentService.getPendingStudentsByCollege(collegeId).catch(() => [])
      ]);
      const drives = drivesData || [];
      const vacancies = vacanciesData || [];
      const apps = appsData || [];
      const pending = pendingData || [];
      setStats({
        drives: drives.length,
        vacancies: vacancies.length,
        applications: apps.length,
        shortlisted: apps.filter(a => a.IsShortlisted).length,
        pendingStudents: pending.length
      });
      setRecentDrives(drives.slice(0, 5));
      setRecentVacancies(vacancies.slice(0, 5));
      setRecentApplications(apps.slice(0, 5));
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : '-');

  if (!collegeId) {
    return (
      <div className="coordinator-dashboard-home">
        <p>Coordinator session not found. Please log in again.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="coordinator-dashboard-home">
        <div className="dashboard-welcome"><h1>Coordinator Portal</h1><p>Loading...</p></div>
      </div>
    );
  }

  return (
    <div className="coordinator-dashboard-home">
      <div className="dashboard-welcome">
        <h1>Welcome to Coordinator Portal</h1>
        <p>Manage placement drives, vacancies, and view applications for your college.</p>
      </div>

      <div className="stats-grid">
        <Link to="/coordinator-portal/pending-students" className="stat-card stat-pending">
          <span className="stat-icon">👥</span>
          <span className="stat-value">{stats.pendingStudents}</span>
          <span className="stat-label">Pending Students</span>
        </Link>
        <Link to="/coordinator-portal/placement-drives" className="stat-card">
          <span className="stat-icon">📋</span>
          <span className="stat-value">{stats.drives}</span>
          <span className="stat-label">Placement Drives</span>
        </Link>
        <Link to="/coordinator-portal/vacancies" className="stat-card">
          <span className="stat-icon">💼</span>
          <span className="stat-value">{stats.vacancies}</span>
          <span className="stat-label">Vacancies</span>
        </Link>
        <Link to="/coordinator-portal/applications" className="stat-card">
          <span className="stat-icon">📄</span>
          <span className="stat-value">{stats.applications}</span>
          <span className="stat-label">Applications</span>
        </Link>
        <div className="stat-card stat-card-info">
          <span className="stat-icon">⭐</span>
          <span className="stat-value">{stats.shortlisted}</span>
          <span className="stat-label">Shortlisted</span>
        </div>
      </div>

      <div className="dashboard-sections">
        <div className="dashboard-section">
          <h3>Recent Placement Drives</h3>
          {recentDrives.length === 0 ? (
            <p className="empty-msg">No placement drives yet. <Link to="/coordinator-portal/placement-drives">Add one</Link>.</p>
          ) : (
            <ul className="recent-list">
              {recentDrives.map((d) => (
                <li key={d.Id}>
                  <Link to="/coordinator-portal/placement-drives" className="recent-item-link">{d.Title}</Link>
                  <span className="date">{formatDate(d.LastDate)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="dashboard-section">
          <h3>Recent Vacancies</h3>
          {recentVacancies.length === 0 ? (
            <p className="empty-msg">No vacancies yet. <Link to="/coordinator-portal/vacancies">Add one</Link>.</p>
          ) : (
            <ul className="recent-list">
              {recentVacancies.map((v) => (
                <li key={v.Id}>
                  <Link to="/coordinator-portal/vacancies" className="recent-item-link">{v.Title}</Link>
                  <span className="date">{v.Company?.Name} · {formatDate(v.LastDate)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="dashboard-section">
          <h3>Recent Applications</h3>
          {recentApplications.length === 0 ? (
            <p className="empty-msg">No applications yet.</p>
          ) : (
            <ul className="recent-list">
              {recentApplications.map((a) => (
                <li key={a.Id}>
                  <Link to="/coordinator-portal/applications" className="recent-item-link">
                    <strong>{a.Student?.Name}</strong> – {a.Vacancy?.Title}
                  </Link>
                  <span className={`status ${a.IsShortlisted ? 'shortlisted' : ''}`}>
                    {a.IsShortlisted ? '⭐ Shortlisted' : 'Pending'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default CoordinatorDashboardHome;
